import { useCallback, useEffect, useMemo, useState } from "react";
import { manualWorkOrderBindingSchema, manualWorkOrderBindingStateSchema, type ManualWorkOrderBindingState,
  type PendingWorkOrderCandidate } from "../../../shared/manual-packaging/pending-work-order-check";
import { apiRequest } from "../../shared/api/client";
import type { PackagingPlanRow } from "./week39-types";

function responseBinding(value: unknown, expectedSourceSha256: string) {
  if (!value || typeof value !== "object" || !("binding" in value) || !("sourceSha256" in value)
    || value.sourceSha256 !== expectedSourceSha256) {
    throw new Error("Sunucu iş emri bağlantısı için geçerli yanıt vermedi.");
  }
  return manualWorkOrderBindingSchema.parse(value.binding);
}

function assertExpectedBinding(binding: ManualWorkOrderBindingState, expected: {
  sourceRow: number; productCode: string; workOrderNo: string | null; version: number; operationId: string;
}) {
  if (binding.sourceRow !== expected.sourceRow || binding.productCode !== expected.productCode
    || binding.workOrderNo !== expected.workOrderNo || binding.version !== expected.version
    || binding.operationId !== expected.operationId) {
    throw new Error("İş emri bağlantısı beklenen kayıt ve sürümle doğrulanamadı. Planı yenileyin.");
  }
  return binding;
}

export function useWeek39WorkOrderBinding(weekStart: string) {
  const [state, setState] = useState<{ canBind: boolean; sourceSha256: string | null; bindings: ManualWorkOrderBindingState[] }>({
    canBind: false, sourceSha256: null, bindings: [],
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const endpoint = `/api/sarf39/manual-packaging/work-order-binding?weekStart=${encodeURIComponent(weekStart)}`;

  const load = useCallback(async () => {
    if (!weekStart) {
      const empty = { canBind: false, sourceSha256: null, bindings: [] as ManualWorkOrderBindingState[] };
      setState(empty);
      return empty;
    }
    setLoading(true);
    try {
      const next = manualWorkOrderBindingStateSchema.parse(await apiRequest<unknown>(endpoint, { cache: "no-store" }));
      setState(next);
      setError("");
      return next;
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "İş emri bağlantıları alınamadı.");
      throw cause;
    } finally {
      setLoading(false);
    }
  }, [endpoint, weekStart]);

  useEffect(() => {
    let active = true;
    if (!weekStart) { setState({ canBind: false, sourceSha256: null, bindings: [] }); return; }
    setLoading(true);
    void apiRequest<unknown>(endpoint, { cache: "no-store" }).then((result) => {
      if (!active) return;
      setState(manualWorkOrderBindingStateSchema.parse(result));
      setError("");
    }).catch((cause: unknown) => {
      if (active) {
        setState({ canBind: false, sourceSha256: null, bindings: [] });
        setError(cause instanceof Error ? cause.message : "İş emri bağlantıları alınamadı.");
      }
    }).finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [endpoint, weekStart]);

  const bindings = useMemo<ReadonlyMap<number, ManualWorkOrderBindingState>>(
    () => new Map(state.bindings.map((binding) => [binding.sourceRow, binding])), [state.bindings]);
  const remember = useCallback((binding: ManualWorkOrderBindingState) => {
    setState((previous) => ({ ...previous, bindings: [
      ...previous.bindings.filter((item) => item.sourceRow !== binding.sourceRow), binding,
    ].sort((a, b) => a.sourceRow - b.sourceRow) }));
  }, []);
  const reconcile = useCallback(async (sourceRow: number, expectedVersion: number,
    expectedWorkOrderNo: string | null, expectedSourceSha256: string, operationId: string, cause: unknown) => {
    try {
      const latest = await load();
      const binding = latest.bindings.find((item) => item.sourceRow === sourceRow);
      if (latest.sourceSha256 === expectedSourceSha256 && binding
        && binding.version === expectedVersion + 1 && binding.workOrderNo === expectedWorkOrderNo
        && binding.operationId === operationId) return binding;
    } catch { throw cause; }
    throw cause;
  }, [load]);

  const bind = useCallback(async (plan: PackagingPlanRow, workOrderNo: string, expectedSourceSha256: string,
    expectedCandidate: PendingWorkOrderCandidate) => {
    const current = bindings.get(plan.sourceRow);
    if (plan.manualWorkOrderBinding && (!current || current.version !== plan.manualWorkOrderBinding.version
      || current.workOrderNo !== plan.workOrderNo)) {
      throw new Error("İş emri bağlantısı siz karşılaştırırken değişti. Planı yenileyip yeniden inceleyin.");
    }
    if (!plan.manualWorkOrderBinding && current?.workOrderNo) {
      throw new Error("Bu plan satırına başka bir iş emri bağlandı. Planı yenileyip yeniden inceleyin.");
    }
    const expectedVersion = plan.manualWorkOrderBinding?.version ?? current?.version ?? 0;
    const operationId = crypto.randomUUID();
    try {
      const binding = responseBinding(await apiRequest<unknown>(endpoint, {
        method: "PUT", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ sourceRow: plan.sourceRow, workOrderNo, expectedVersion,
          expectedSourceSha256, expectedCandidate, comparisonConfirmed: true, operationId }),
      }), expectedSourceSha256);
      assertExpectedBinding(binding, { sourceRow: plan.sourceRow, productCode: plan.productCode,
        workOrderNo, version: expectedVersion + 1, operationId });
      remember(binding);
      return binding;
    } catch (cause) {
      const binding = await reconcile(plan.sourceRow, expectedVersion, workOrderNo,
        expectedSourceSha256, operationId, cause);
      remember(binding);
      return binding;
    }
  }, [bindings, endpoint, reconcile, remember]);

  const remove = useCallback(async (plan: PackagingPlanRow, expectedSourceSha256: string) => {
    const current = bindings.get(plan.sourceRow);
    if (!current?.workOrderNo || !plan.manualWorkOrderBinding || current.version !== plan.manualWorkOrderBinding.version
      || current.workOrderNo !== plan.workOrderNo) {
      throw new Error("Kaldırılacak bağlantı değişti. Planı yenileyip yeniden inceleyin.");
    }
    const operationId = crypto.randomUUID();
    try {
      const binding = responseBinding(await apiRequest<unknown>(endpoint, {
        method: "DELETE", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ sourceRow: plan.sourceRow, expectedVersion: current.version, expectedSourceSha256, operationId }),
      }), expectedSourceSha256);
      assertExpectedBinding(binding, { sourceRow: plan.sourceRow, productCode: plan.productCode,
        workOrderNo: null, version: current.version + 1, operationId });
      remember(binding);
      return binding;
    } catch (cause) {
      const binding = await reconcile(plan.sourceRow, current.version, null,
        expectedSourceSha256, operationId, cause);
      remember(binding);
      return binding;
    }
  }, [bindings, endpoint, reconcile, remember]);

  return { canBind: state.canBind, sourceSha256: state.sourceSha256, bindings, loading, error, reload: load, bind, remove };
}
